import React, { useState } from 'react';

import { EmpresaFiscal } from '../../../services/ativarEmissaoService';
import * as S from '../styles';

interface Step2Props {
  company: EmpresaFiscal | null;
  loading: boolean;
  onSubmit: (file: File, password: string) => Promise<void> | void;
}

const formatValidade = (validade?: string | null): string => {
  if (!validade) return '';
  const date = new Date(validade);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('pt-BR');
};

const statusTone = (status?: string | null): 'ok' | 'warn' | 'error' => {
  if (status === 'valido') return 'ok';
  if (status === 'invalido' || status === 'expirado') return 'error';
  return 'warn';
};

/**
 * Passo 2 — Certificado A1 (Story 1-7 / AC9). Input de arquivo nativo (.pfx/.p12) + senha →
 * POST /empresa/:id/certificado. A senha vive apenas em estado local e é
 * limpa após o envio (AC10) — nunca persiste.
 */
const Step2Certificado: React.FC<Step2Props> = ({
  company,
  loading,
  onSubmit,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');

  const handleSubmit = async (): Promise<void> => {
    if (!file || !password) return;
    await onSubmit(file, password);
    // AC10: a senha não persiste no frontend após o envio.
    setPassword('');
    setFile(null);
  };

  const validade = formatValidade(company?.certificado_validade);

  return (
    <S.StepCard>
      <h3>2. Certificado digital (A1)</h3>

      {company?.certificado_status && (
        <S.StatusBadge tone={statusTone(company.certificado_status)}>
          Certificado: {company.certificado_status}
          {validade ? ` · válido até ${validade}` : ''}
        </S.StatusBadge>
      )}

      {/* Seletor de arquivo NATIVO — o certificado é um binário (.pfx/.p12),
          não uma imagem. NÃO usar o FileInput de imagem (faz preview <img> e
          compressão, que estourava o layout e poderia corromper o arquivo). */}
      <div className="field">
        <label htmlFor="certificado-file">
          Arquivo do certificado (.pfx ou .p12 — até 10MB)
        </label>
        <input
          id="certificado-file"
          type="file"
          accept=".pfx,.p12"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file && (
          <small>
            Selecionado: <strong>{file.name}</strong> (
            {(file.size / 1024).toFixed(0)} KB)
          </small>
        )}
      </div>

      <div className="field">
        <label htmlFor="certificado-password">Senha do certificado</label>
        <input
          id="certificado-password"
          type="password"
          autoComplete="off"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Senha do .pfx"
        />
      </div>

      <S.Button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !file || !password}
      >
        {loading ? 'Enviando…' : 'Enviar certificado'}
      </S.Button>
    </S.StepCard>
  );
};

export default Step2Certificado;
